const LINE_COLOR   = '#2b2821';
const LINE_OPACITY = 0.85;
const STROKE_WIDTH = 1.5;
const DASH_ARRAY   = '2 3';
const ANCHOR_R     = 5.5;
const TRIM_EPSILON = 0.5;

// Gap below heading bottom where the horizontal line sits
const HEADING_GAP  = 6;

function getAnchorCenter(anchorEl) {
  const cs      = getComputedStyle(anchorEl);
  // 767 is where the layout switches to the Figma mobile frame (375). Above it
  // the panel keeps the two-column desktop arrangement, so the desktop dot
  // positions are the correct ones.
  const isMob   = window.innerWidth <= 767;
  const topVar  = isMob
    ? cs.getPropertyValue('--anchor-top-mobile').trim()  || cs.getPropertyValue('--anchor-top').trim()
    : cs.getPropertyValue('--anchor-top').trim();
  const leftVar = isMob
    ? cs.getPropertyValue('--anchor-left-mobile').trim() || cs.getPropertyValue('--anchor-left').trim()
    : cs.getPropertyValue('--anchor-left').trim();

  const topPct  = parseFloat(topVar)  / 100;
  const leftPct = parseFloat(leftVar) / 100;

  const wrapper = anchorEl.closest('.landing-page-capsule-features__capsule');
  if (!wrapper) return null;

  const wr = wrapper.getBoundingClientRect();
  if (wr.width === 0 || wr.height === 0) return null;

  return {
    x: wr.left + window.scrollX + wr.width  * leftPct,
    y: wr.top  + window.scrollY + wr.height * topPct,
  };
}

// Line always starts from heading's left edge, HEADING_GAP px below heading bottom.
// This point is always above the description text, so the horizontal leg
// never overlaps it.
function getLineStartPoint(headingEl) {
  const hr = headingEl.getBoundingClientRect();
  return {
    x: hr.left   + window.scrollX,
    y: hr.bottom + window.scrollY + HEADING_GAP,
  };
}

function pathD(pts) {
  if (!pts || pts.length < 2) return '';
  return pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
}

function makePath(a, center) {
  const trimR = ANCHOR_R + STROKE_WIDTH + TRIM_EPSILON;
  const dx    = Math.abs(center.x - a.x);

  // The line ALWAYS starts just below the heading (a.y).
  // It goes RIGHT to the dot's X, then DOWN or UP to the dot.
  //
  // Special case: if the dot is at exactly the same Y as a.y → straight line,
  // trimmed on X axis. This only happens when the merchant has positioned the
  // dot precisely level with the heading gap — no threshold fudging.
  //
  // In all other cases we use an L-shape that stays above the description text.

  const dy = center.y - a.y; // signed: positive = dot is below start

  // Straight horizontal: dot is at exactly the line-start Y (within 2px)
  if (Math.abs(dy) <= 2) {
    const xDir = center.x > a.x ? 1 : -1;
    return [
      { x: a.x,                     y: a.y },
      { x: center.x - xDir * trimR, y: a.y },
    ];
  }

  // L-shape for everything else:
  // Leg 1 — horizontal at heading-gap Y from left edge to dot's X
  // Leg 2 — vertical from heading-gap Y down/up to dot
  // Arrives at circle vertically → trim on Y axis
  if (dx >= 0.5) {
    const corner     = { x: center.x, y: a.y };
    const yDir       = dy > 0 ? 1 : -1;
    const trimmedEnd = { x: center.x, y: center.y - yDir * trimR };
    return [a, corner, trimmedEnd];
  }

  // Pure vertical fallback (dot directly above/below start, same X)
  const yDir = dy > 0 ? 1 : -1;
  return [a, { x: center.x, y: center.y - yDir * trimR }];
}

function createOverlay() {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  Object.assign(svg.style, {
    position: 'absolute',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    pointerEvents: 'none',
    zIndex: '99',
    overflow: 'visible',
  });
  if (getComputedStyle(document.body).position === 'static') {
    document.body.style.position = 'relative';
  }
  document.body.appendChild(svg);

  let nodes = [];

  function clear() { nodes.forEach(n => n.remove()); nodes = []; }

  function draw(root) {
    clear();

    const headings = root.querySelectorAll('[data-line-start]');
    if (!headings.length) return;

    const anchorMap = new Map();
    root.querySelectorAll('[data-capsule-anchor]').forEach(el => {
      const k = el.getAttribute('data-feature-index');
      if (k != null) anchorMap.set(k, el);
    });

    headings.forEach(h => {
      const anchor = anchorMap.get(h.getAttribute('data-feature-index'));
      if (!anchor) return;

      const center = getAnchorCenter(anchor);
      if (!center) return;

      const a = getLineStartPoint(h);
      const d = pathD(makePath(a, center));
      if (!d) return;

      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', d);
      path.setAttribute('fill', 'none');
      path.setAttribute('stroke', LINE_COLOR);
      path.setAttribute('stroke-width', STROKE_WIDTH);
      path.setAttribute('stroke-opacity', LINE_OPACITY);
      path.setAttribute('stroke-dasharray', DASH_ARRAY);
      path.setAttribute('stroke-linecap', 'butt');
      svg.appendChild(path);
      nodes.push(path);

      // Circle always drawn at exact center
      const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      circle.setAttribute('cx', center.x);
      circle.setAttribute('cy', center.y);
      circle.setAttribute('r',  ANCHOR_R);
      circle.setAttribute('fill', 'none');
      circle.setAttribute('stroke', LINE_COLOR);
      circle.setAttribute('stroke-width', STROKE_WIDTH);
      svg.appendChild(circle);
      nodes.push(circle);
    });
  }

  return {
    draw,
    destroy() { clear(); svg.remove(); },
  };
}

function bindSection(root) {
  const overlay = createOverlay();
  const update  = () => requestAnimationFrame(() => overlay.draw(root));

  const ro = new ResizeObserver(update);
  root.querySelectorAll(
    '.landing-page-capsule-features__capsule, .landing-page-capsule-features__right-grid'
  ).forEach(el => ro.observe(el));
  ro.observe(root);

  document.addEventListener('lazyloaded', e => { if (root.contains(e.target)) update(); }, true);
  window.addEventListener('resize', update);

  update();
  setTimeout(update, 100);
  setTimeout(update, 300);
  setTimeout(update, 600);

  if (document.readyState !== 'complete') {
    window.addEventListener('load', update, { once: true });
  }

  return () => {
    ro.disconnect();
    window.removeEventListener('resize', update);
    overlay.destroy();
  };
}

function init() {
  document.querySelectorAll('.landing-page-capsule-features').forEach(root => {
    if (root._capsuleDestroy) return;
    root._capsuleDestroy = bindSection(root);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}